/**
 * Orders Loaders - TanStack Router data loading
 * Populates Redux store before component renders
 * Uses: ordersApi (infrastructure), ordersActions (store)
 */

import { ordersApi } from '../api/orders-api'
import { ordersActions } from '../../store/orders.slice'
import type { OrdersSearchParams } from '../../domain/filter-config'
import { ordersFilterConfig, DEFAULT_ORDERS_FILTER } from '../../domain/filter-config'
import type { OrdersFilter } from '../../domain/types/order.types'
import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'

// Router context type - will be provided by app
interface RouterContext {
  store: {
    dispatch: Dispatch<UnknownAction>
    getState: () => unknown
  }
}

interface OrdersStoreState {
  orders: { filter: OrdersFilter }
}

/**
 * Compare two filter objects by VALUE (not reference), IGNORING pagination fields.
 *
 * Pagination (page / pageSize) is intentionally excluded from the comparison
 * because the loader may run with a stale URL snapshot (e.g. the URL still shows
 * page=1 after the component has already advanced to page=3 via a Redux dispatch).
 * If we included page in the comparison, the loader would see page:1 ≠ page:3 and
 * dispatch setFilter({ page: 1 }), resetting pagination — the exact race condition
 * this guard exists to prevent.
 *
 * Pagination state is owned by the component's fetch useEffect and dispatch chain,
 * NOT by the loader. The loader is only responsible for initialising non-pagination
 * filter fields (status, dateRange, search, etc.) from URL search params.
 *
 * Also treats `''`, `null`, and `undefined` as equivalent so that empty-string
 * fields in Redux don't cause false inequality against `undefined` in the URL.
 */
function filtersAreEqual(a: OrdersFilter, b: OrdersFilter): boolean {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page: _ap, pageSize: _aps, ...aRest } = a
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page: _bp, pageSize: _bps, ...bRest } = b
  const normalize = (_key: string, val: unknown) =>
    val === null || val === undefined || val === '' ? undefined : val
  return JSON.stringify(aRest, normalize) === JSON.stringify(bRest, normalize)
}

/**
 * Load orders list for /orders route
 * Sets up initial filter state from URL params, component handles fetching
 * Supports all filter params from URL
 */
export const ordersLoader = async ({
  context,
  initialSearch,
}: {
  context: RouterContext
  initialSearch?: OrdersSearchParams
}) => {
  // Check if store is available in context
  if (!context?.store) {
    console.error('[ordersLoader] Store not available in router context')
    return null
  }

  const { store } = context

  // Parse URL search params into filter state
  if (initialSearch && Object.keys(initialSearch).some(k => initialSearch[k as keyof OrdersSearchParams] !== undefined)) {
    // Convert OrdersSearchParams to Record<string, string | null>
    const params: Record<string, string | null> = {}
    for (const [key, value] of Object.entries(initialSearch)) {
      params[key] = value ?? null
    }

    // Deserialize and validate
    const urlFilter = ordersFilterConfig.deserialize(params)
    const mergedFilter = { ...DEFAULT_ORDERS_FILTER, ...urlFilter }
    const validatedFilter = ordersFilterConfig.validate?.(mergedFilter) ?? mergedFilter

    // Skip dispatch if the filter values haven't actually changed.
    // loaderDeps re-runs this loader on every URL change, which creates new array
    // references for status/products/deliveryAddresses even when values are identical.
    // Skipping redundant dispatches prevents takeLatest from cancelling in-flight fetches.
    const currentFilter = (store.getState() as OrdersStoreState).orders?.filter
    if (currentFilter && filtersAreEqual(currentFilter, validatedFilter)) {
      return null
    }

    store.dispatch(ordersActions.setFilter(validatedFilter))
  }

  // Don't fetch here - let the component useEffect handle it
  // This prevents duplicate requests
  return null
}

/**
 * Load single order for /orders/:orderId route
 */
export const orderDetailLoader = async ({
  context,
  params,
}: {
  context: RouterContext
  params: { orderId: string }
}) => {
  const { store } = context

  try {
    const order = await ordersApi.getOrder(params.orderId)

    if (order) {
      store.dispatch(ordersActions.setSelectedOrder(order))
    } else {
      store.dispatch(ordersActions.setError('Order not found'))
    }
  } catch (error) {
    store.dispatch(ordersActions.setError((error as Error).message))
  }

  return null
}

/**
 * Load orders widget data (for dashboard)
 */
export const ordersWidgetLoader = async ({ context }: { context: RouterContext }) => {
  const { store } = context

  try {
    const stats = await ordersApi.getOrderStats()
    store.dispatch(ordersActions.setStats(stats))
  } catch (error) {
    // Silent fail for widget - don't block dashboard
    console.error('Failed to load orders widget:', error)
  }

  return null
}
